import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Camera,
  Clock,
  Calendar,
  Package,
  Building2,
  User,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

const BatchScanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannedBatch, setScannedBatch] = useState(null);
  const [error, setError] = useState(null);

  // Mock scan function
  const handleScan = () => {
    setScanning(true);
    setTimeout(() => {
      // Simulate successful scan
      const mockBatch = {
        batchNumber: 'CK1M202400123',
        product: 'Sauce Base',
        location: 'Central Kitchen 1',
        productionDate: new Date().toISOString(),
        expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        chef: 'Ahmed Ali',
        status: 'Active',
        qualityChecks: [
          { parameter: 'Temperature', status: 'pass' },
          { parameter: 'pH Level', status: 'pass' },
          { parameter: 'Moisture Content', status: 'pass' }
        ]
      };
      setScannedBatch(mockBatch);
      setScanning(false);
      setError(null);
    }, 2000);
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    // Simulate batch lookup
    if (searchTerm.length > 0) {
      handleScan();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Batch Scanner</h1>
          <p className="text-gray-500 mt-2">Scan or search for batch information</p>
        </div>

        {/* Search and Scan Controls */}
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 mb-8">
          <form onSubmit={handleManualSearch} className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Enter batch number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </form>

          <div className="text-center">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Camera className="w-5 h-5 mr-2" />
              {scanning ? 'Scanning...' : 'Scan Batch Label'}
            </button>
          </div>
        </div>

        {/* Scan Result */}
        {error && (
          <div className="bg-rose-50 text-rose-700 p-4 rounded-xl mb-8">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {scannedBatch && (
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold">Batch Information</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                scannedBatch.status === 'Active' 
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : 'bg-rose-100 text-rose-700 border border-rose-200'
              }`}>
                {scannedBatch.status}
              </span>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Batch Number</p>
                  <p className="font-medium">{scannedBatch.batchNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Product</p>
                  <p className="font-medium">{scannedBatch.product}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Production Date</p>
                  <p className="font-medium">
                    {new Date(scannedBatch.productionDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Expiry Date</p>
                  <p className="font-medium">
                    {new Date(scannedBatch.expiryDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <p className="font-medium">{scannedBatch.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Chef</p>
                  <p className="font-medium">{scannedBatch.chef}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Quality Checks</p>
                <div className="grid grid-cols-3 gap-4">
                  {scannedBatch.qualityChecks.map((check, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg ${
                        check.status === 'pass'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-rose-50 text-rose-700'
                      }`}
                    >
                      <p className="text-sm font-medium">{check.parameter}</p>
                      <p className="text-xs capitalize">{check.status}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t flex justify-end">
                <button
                  onClick={() => navigate(`/batch-control/${scannedBatch.batchNumber}`)}
                  className="flex items-center text-indigo-600 hover:text-indigo-700"
                >
                  View Full Details
                  <ExternalLink className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchScanner;